import { inject, injectable, interfaces } from 'inversify';
import { IParameterDto } from '../../../__legacy__/helpers/parameters.helpers';
import { ACDLError, CUSTOM_ERROR_CODES } from '../../../__legacy__/models/error.model';
import { IApiService } from '../../../__legacy__/services/api.service.base';
import { IApiServiceTraining } from '../../../__legacy__/services/api.service.training';
import {
  IInferenceDetailsDto,
  IInferenceStartDto,
  IInfoEditDto,
  IJobEstimatedCostDto,
  IJobImagePredictionsDto,
  IJobMetricsDto,
  IJobStatusDto,
  IJobThresholdDiscrepancyDto,
  IJobThresholdDto,
  ISubTrainingJobDto,
  ITrainingDetailsDto,
  ITrainingStartDto,
} from '../jobs.api.model';
import { IValidJobType, JOB_RESOURCE_TYPE, JOB_TYPE } from '../jobs.model';
import { getJobApiPrefix } from './utils';

export const JOB_DETAILS_CUSTOM_ERRORS = [
  CUSTOM_ERROR_CODES.COST_LIMIT_EXCEEDED,
  CUSTOM_ERROR_CODES.ANNOTATIONS_MISMATCH,
  CUSTOM_ERROR_CODES.WRONG_IMAGE_SIZE,
  CUSTOM_ERROR_CODES.EMPTY_TRAIN_DATASET,
  CUSTOM_ERROR_CODES.MISSING_ANNOTATIONS_TRAIN,
  CUSTOM_ERROR_CODES.MISSING_ANNOTATIONS,
  CUSTOM_ERROR_CODES.EMPTY_VALID_DATASET,
  CUSTOM_ERROR_CODES.TOO_MANY_QUESTIONS,
  CUSTOM_ERROR_CODES.VALIDATION_ANNOTATIONS_MISMATCH,
  CUSTOM_ERROR_CODES.TRAIN_ANNOTATIONS_MISMATCH,
  CUSTOM_ERROR_CODES.QUESTION_ANNOTATION_MISMATCH,
  CUSTOM_ERROR_CODES.GOOD_IMAGE_WITH_DEFECTS,
  CUSTOM_ERROR_CODES.TRAIN_VALIDATION_ANNOTATIONS_MISMATCH,
  CUSTOM_ERROR_CODES.WORKSPACE_SPACE_LIMIT_REACHED,
];

export interface IJobDetailsApiService {
  getJobDetailsAsync<T extends IValidJobType>(
    workspaceId: string,
    jobId: string,
    jobType: T
  ): Promise<(T extends JOB_TYPE.INFERENCE ? IInferenceDetailsDto : ITrainingDetailsDto) | ACDLError>;
  postJobEstimateCostAsync<T extends IValidJobType>(
    workspaceId: string,
    request: T extends JOB_TYPE.INFERENCE ? IInferenceStartDto : ITrainingStartDto,
    jobType: T,
    params: T extends JOB_TYPE.INFERENCE ? { training_job_id: string } : { auto_training: boolean }
  ): Promise<IJobEstimatedCostDto | ACDLError>;
  getJobImagePredictionsAsync(workspaceId: string, jobId: string, imagesIds: string[], jobType: IValidJobType): Promise<IJobImagePredictionsDto | ACDLError>;
  getJobStatusAsync(workspaceId: string, jobId: string, jobType: IValidJobType): Promise<IJobStatusDto | ACDLError>;
  getSubTrainingJobsAsync(workspaceId: string, trainingId: string): Promise<ISubTrainingJobDto[] | ACDLError>;
  getTrainingThresholdAsync(workspaceId: string, trainingId: string): Promise<IParameterDto | ACDLError>;
  getJobRecalculateMetricsAsync(
    workspaceId: string,
    jobId: string,
    request: IJobThresholdDto,
    jobType: IValidJobType
  ): Promise<IJobMetricsDto['metrics'] | ACDLError>;
  postJobThresholdsAsync(workspaceId: string, jobId: string, request: IJobThresholdDto, jobType: IValidJobType): Promise<string | ACDLError>;
  getJobThresholdDiscrepancy(workspaceId: string, jobId: string, jobType: JOB_TYPE.INFERENCE): Promise<IJobThresholdDiscrepancyDto | ACDLError>;
  getResourceUrl(workspaceId: string, jobId: string, jobType: IValidJobType, resourceId: string, resourceType: JOB_RESOURCE_TYPE): string;
  postJobInfoEditAsync(workspaceId: string, jobId: string, jobType: IValidJobType, request: IInfoEditDto): Promise<string | ACDLError>;
}

export namespace IJobDetailsApiService {
  export const $: interfaces.ServiceIdentifier<IJobDetailsApiService> = Symbol('JOB_DETAILS_API_SERVICE');
}

@injectable()
export class JobDetailsApiService implements IJobDetailsApiService {
  constructor(@inject(IApiServiceTraining.$) private readonly apiTrainingService: IApiService) {}

  getJobDetailsAsync = async <T extends Exclude<JOB_TYPE, JOB_TYPE.UNKNOWN>>(
    workspaceId: string,
    jobId: string,
    jobType: T
  ): Promise<(T extends JOB_TYPE.INFERENCE ? IInferenceDetailsDto : ITrainingDetailsDto) | ACDLError> => {
    return this.apiTrainingService.getAsync(`/workspaces/${workspaceId}/${getJobApiPrefix(jobType)}/${jobId}`);
  };

  postJobEstimateCostAsync = async <T extends IValidJobType>(
    workspaceId: string,
    request: T extends JOB_TYPE.INFERENCE ? IInferenceStartDto : ITrainingStartDto,
    jobType: T,
    params: T extends JOB_TYPE.INFERENCE ? { training_job_id: string } : { auto_training: boolean }
  ): Promise<IJobEstimatedCostDto | ACDLError> => {
    return this.apiTrainingService.postAsync(`/workspaces/${workspaceId}/cost-estimation/${getJobApiPrefix(jobType)}`, request, {
      params,
    });
  };

  getJobImagePredictionsAsync = async (
    workspaceId: string,
    jobId: string,
    imagesIds: string[],
    jobType: IValidJobType
  ): Promise<IJobImagePredictionsDto | ACDLError> => {
    return this.apiTrainingService.postAsync(`/workspaces/${workspaceId}/${getJobApiPrefix(jobType)}/${jobId}/image-metrics`, imagesIds);
  };

  getJobStatusAsync = async (workspaceId: string, jobId: string, jobType: IValidJobType): Promise<IJobStatusDto | ACDLError> => {
    return this.apiTrainingService.getAsync(`/workspaces/${workspaceId}/${getJobApiPrefix(jobType)}/${jobId}/status`);
  };

  getSubTrainingJobsAsync = async (workspaceId: string, trainingId: string): Promise<ISubTrainingJobDto[] | ACDLError> => {
    return this.apiTrainingService.getAsync(`/workspaces/${workspaceId}/training-jobs/${trainingId}/jobs-auto`);
  };

  getTrainingThresholdAsync = async (workspaceId: string, trainingId: string): Promise<IParameterDto | ACDLError> => {
    return this.apiTrainingService.getAsync(`/workspaces/${workspaceId}/training-jobs/${trainingId}/inference/threshold`);
  };

  getJobRecalculateMetricsAsync = async (
    workspaceId: string,
    jobId: string,
    request: IJobThresholdDto,
    jobType: IValidJobType
  ): Promise<IJobMetricsDto['metrics'] | ACDLError> => {
    return this.apiTrainingService.getAsync(`/workspaces/${workspaceId}/${getJobApiPrefix(jobType)}/${jobId}/metrics`, { params: request });
  };

  postJobThresholdsAsync = async (workspaceId: string, jobId: string, request: IJobThresholdDto, jobType: IValidJobType): Promise<string | ACDLError> => {
    return this.apiTrainingService.postAsync(`/workspaces/${workspaceId}/${getJobApiPrefix(jobType)}/${jobId}/threshold`, request);
  };

  getJobThresholdDiscrepancy = async (workspaceId: string, jobId: string, jobType: JOB_TYPE.INFERENCE): Promise<IJobThresholdDiscrepancyDto | ACDLError> => {
    return this.apiTrainingService.getAsync(`/workspaces/${workspaceId}/${getJobApiPrefix(jobType)}/${jobId}/threshold`);
  };

  getResourceUrl(workspaceId: string, jobId: string, jobType: IValidJobType, resourceId: string, resourceType: JOB_RESOURCE_TYPE): string {
    return this.apiTrainingService.getUrl(`/workspaces/${workspaceId}/${getJobApiPrefix(jobType)}/${jobId}/${resourceType}/${resourceId}`);
  }

  async postJobInfoEditAsync(workspaceId: string, jobId: string, jobType: IValidJobType, request: IInfoEditDto): Promise<string | ACDLError> {
    return await this.apiTrainingService.postAsync(`/workspaces/${workspaceId}/${getJobApiPrefix(jobType)}/${jobId}/name-and-description`, request);
  }
}
